import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Copy, ExternalLink, Info } from 'lucide-react';
import { buildFormEmbedSnippet } from './embedSnippet';
import { DEVICE_VALUES, DEVICE_LABELS } from '../../lib/deviceConstants';

export default function EmbedCodeModal({ open, onClose, title, slug }) {
    const [device, setDevice] = useState('desktop');

    if (!slug) return null;

    const snippet = buildFormEmbedSnippet({ slug, device });

    if (!snippet) return null;

    const publicUrl = snippet.publicUrl;
    const iframeCode = snippet.iframeCode;
    const scriptCode = snippet.scriptCode;

    const copyPublicUrl = () => {
        navigator.clipboard.writeText(publicUrl);
        toast.success('Public URL copied');
    };

    const copyIframe = () => {
        navigator.clipboard.writeText(iframeCode);
        toast.success('iframe code copied');
    };

    const copyScript = () => {
        navigator.clipboard.writeText(scriptCode);
        toast.success('Script tag copied');
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader><DialogTitle>Embed Code — {title}</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                    <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1">Device Mode</label>
                        <select
                            value={device}
                            onChange={e => setDevice(e.target.value)}
                            className="w-full text-sm px-3 py-2 rounded border border-stone-200 bg-white"
                        >
                            {DEVICE_VALUES.map(d => (
                                <option key={d} value={d}>{DEVICE_LABELS[d]}</option>
                            ))}
                        </select>
                        <p className="text-xs text-stone-500 mt-1">
                            Constrains the form width for the target device.
                        </p>
                        <div className="mt-2 flex gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
                            <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-blue-700 leading-relaxed">
                                The <code className="font-mono">?device=</code> query parameter controls the form's maximum
                                width inside the iframe. <strong>Phone</strong> → 375px, <strong>Tablet</strong> → 768px,
                                <strong> Desktop</strong> → no limit. Both snippets impose zero fixed sizing — the iframe
                                height auto-adapts to the form content, and the width defaults to 100% of your container
                                (overridable via CSS). No scrollbar, ever.
                            </p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1">Public URL</label>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-stone-50 px-3 py-2 rounded border border-stone-200 break-all">
                                {publicUrl}
                            </code>
                            <Button variant="outline" size="sm" onClick={copyPublicUrl}>
                                <Copy className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.open(publicUrl, '_blank', 'noopener')}>
                                <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1">iframe Embed</label>
                        <textarea readOnly rows={3} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200 break-all whitespace-pre-wrap"
                            value={iframeCode} />
                        <Button variant="outline" size="sm" className="mt-1" onClick={copyIframe}>
                            <Copy className="w-3.5 h-3.5 mr-1" /> Copy iframe
                        </Button>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1">Script Tag (auto-resize)</label>
                        <textarea readOnly rows={5} className="w-full text-xs font-mono bg-stone-50 px-3 py-2 rounded border border-stone-200 break-all whitespace-pre-wrap"
                            value={scriptCode} />
                        <Button variant="outline" size="sm" className="mt-1" onClick={copyScript}>
                            <Copy className="w-3.5 h-3.5 mr-1" /> Copy script
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
